import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { CitationSet } from './evidence-citation.model';
import { INLINE_SECTION_MARKER_PATTERN, UNRESOLVED_SECTION_PLACEHOLDER_PATTERN } from './inline-code-reference';

/**
 * Renders a report's Markdown body into sanitized HTML interleaved with resolved citations
 * (Issue #1773).
 *
 * This module replaces the report tab's previous one-liner, which parsed model-authored Markdown
 * with `marked` and handed the result straight to `bypassSecurityTrustHtml()` with no sanitizer in
 * between — unlike chat, which has always run `DOMPurify` first. Two things change, and they are
 * the security substance of this issue:
 *
 * 1. **Sanitize, then trust — the whole document, never a substring.** The body is sanitized against
 *    a closed tag allow-list, and the string that is eventually trusted is that sanitized document
 *    with citation placeholders inserted into its text nodes by this module. It is never a slice of
 *    it.
 *
 *    An earlier version of this module got that wrong, and the distinction is the whole reason this
 *    is written the way it is. It matched citation markers against the *serialized* sanitized HTML
 *    and sliced the string at each match, then trusted each slice. A substring of clean output is
 *    not clean: `colspan` is an allowed attribute, DOMPurify keeps an attribute value whose first
 *    character is not a letter, and a serializer need not escape angle brackets inside an attribute
 *    value. So `<td colspan="[^zz]<img src=x onerror=...>">` survived sanitization intact, and
 *    slicing at the marker cut the attribute open — leaving a slice that *begins* as markup and
 *    creates a live event handler when trusted. Reproduced under jsdom 28.1.0; Chromium happened to
 *    escape the brackets, which is luck rather than a property anyone designed for. Found by
 *    security review of PR #1824 as finding F1.
 *
 * 2. **The body carries no anchors at all.** `a` is deliberately absent from the allow-list, so a
 *    URL written in model prose renders as visible text and never as a link. Combined with the fact
 *    that {@link EvidenceCitationLinkService} only accepts a validated citation record and never a
 *    string, this is what makes "a model-authored URL cannot become a live link" a property of the
 *    code rather than a promise. Every live link in a report body originates from a citation the
 *    resolver accepted.
 *
 * The reader loses nothing real: before this change a model-authored URL was clickable but
 * unverified, which is precisely the failure mode the issue is about. Real destinations now arrive
 * as structured citations, which are checked, labelled by evidence kind, and accessible.
 */

/**
 * Tags a compliance statement may use.
 *
 * A closed allow-list rather than a deny-list, because a deny-list has to be updated every time the
 * HTML spec or a Markdown renderer grows a new capability, and the cost of missing one is an
 * injection. Note the absences: no `a`, no `img`, no `iframe`, no `form`, no `style`, no `script`.
 */
const ALLOWED_TAGS: readonly string[] = Object.freeze([
  'p',
  'br',
  'hr',
  'span',
  'div',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'ins',
  'sub',
  'sup',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'caption',
]);

/**
 * Attributes a compliance statement may use.
 *
 * Only table spans survive, because they carry layout meaning a report genuinely needs. Everything
 * else is dropped, including `class`, `id`, and `style`: a model has no business addressing the
 * application's stylesheet, and every `on*` handler is excluded by construction rather than by
 * enumeration.
 *
 * Note what these two being allowed made possible. DOMPurify keeps an attribute value whose first
 * character is not a letter, so `colspan="[^zz]<img src=x onerror=...>"` passes sanitization with
 * its angle brackets intact. That is harmless in the document, and was not harmless once the
 * serialized document was being sliced — see the module comment above.
 */
const ALLOWED_ATTR: readonly string[] = Object.freeze(['colspan', 'rowspan']);

/**
 * Sentinel delimiters that hide a marker from the Markdown parser.
 *
 * Private-use code points, chosen because Markdown assigns them no meaning and a compliance
 * statement will never contain one. They are inserted before parsing and removed during the text
 * node walk, so no reader ever sees them.
 */
const MARKER_SENTINEL_OPEN = '\uE000';
const MARKER_SENTINEL_CLOSE = '\uE001';

/** The three payload shapes, anchored, as they appear inside a sentinel pair. */
const INLINE_SECTION_PAYLOAD = /^\^sec:([A-Za-z]?\d{3,5}(?:\.\d+)*)$/i;
const UNRESOLVED_PLACEHOLDER_PAYLOAD = /^icc-section:\/\/[A-Za-z0-9.]{1,32}\/([A-Za-z]?\d{3,5}(?:\.\d+)*)$/i;
const CITATION_PAYLOAD = /^\^([A-Za-z0-9_-]{1,64})$/;

/** A protected marker in the parsed document: the sentinel pair around the marker's own payload. */
const SENTINEL_PATTERN = /\uE000([^\uE000\uE001]{1,256})\uE001/g;

/**
 * Markdown regions whose contents are literal: fenced blocks and inline code spans.
 *
 * A marker quoted inside one of these is text a reader is meant to see, so it must not be protected
 * — protecting it would strip its brackets and then the walk, which refuses to substitute inside
 * `pre` and `code`, would leave an invisible sentinel behind instead of the quoted syntax.
 */
const LITERAL_MARKDOWN_REGION = /```[\s\S]*?```|~~~[\s\S]*?~~~|(`+)[^`\n]*\1/g;

/**
 * Hides every marker from Markdown before the document is parsed.
 *
 * THE DEFECT THIS EXISTS FOR: a marker followed immediately by a parenthesis is Markdown link
 * syntax. The statement "IBC 905.4(1)" is marked as `[^sec:905.4](1)`, and `marked` reads that as a
 * link whose text is `^sec:905.4` and whose destination is `1`. The brackets are consumed as
 * delimiters, sanitization then drops the anchor because the body's allow-list has no `a`, and what
 * reaches the reader is the bare string "^sec:905.4" in the middle of a sentence. By the time the
 * text-node walk runs there is no marker left for it to find, so nothing downstream can notice.
 *
 * The marker shape was chosen to LOOK like a footnote so that Markdown would ignore it, and for
 * ordinary prose it does. What was not anticipated is that the shape is also the first half of a
 * link, and the model writes item references in parentheses. Escaping the parenthesis would fix that
 * one collision; hiding the marker from the parser altogether fixes the class, including whatever
 * other punctuation a future statement happens to put next to a marker.
 */
export function protectMarkersFromMarkdown(markdown: string): string {
  let out = '';
  let cursor = 0;
  LITERAL_MARKDOWN_REGION.lastIndex = 0;
  for (
    let region = LITERAL_MARKDOWN_REGION.exec(markdown);
    region !== null;
    region = LITERAL_MARKDOWN_REGION.exec(markdown)
  ) {
    out += encodeMarkers(markdown.slice(cursor, region.index));
    out += region[0];
    cursor = region.index + region[0].length;
  }
  return out + encodeMarkers(markdown.slice(cursor));
}

/** Wraps each marker in the region's text, dropping the brackets Markdown would otherwise claim. */
function encodeMarkers(text: string): string {
  return text.replace(new RegExp(ALL_MARKERS_PATTERN.source, 'gi'), match => {
    const payload = match.startsWith('[') && match.endsWith(']') ? match.slice(1, -1) : match;
    return `${MARKER_SENTINEL_OPEN}${payload}${MARKER_SENTINEL_CLOSE}`;
  });
}

/**
 * Sanitizes model-authored Markdown to HTML.
 *
 * Exported separately from {@link renderReportBody} so the sanitizer configuration can be tested on
 * its own, against hostile input, without also asserting on how citations are resolved.
 */
export async function sanitizeReportMarkdown(markdown: string): Promise<string> {
  if (!markdown) return '';
  const html = await marked.parse(protectMarkersFromMarkdown(markdown));
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR],
    // Keep the text of a disallowed element. Dropping an anchor should leave the sentence readable,
    // not punch a hole in it. Elements in DOMPurify's own FORBID_CONTENTS set — `script` and `style`
    // among them — still lose their contents, which is what keeps stripped CSS and JavaScript from
    // reappearing as visible prose.
    KEEP_CONTENT: true,
    // Deliberately NOT passing USE_PROFILES. DOMPurify derives its allowed-tag set from a profile
    // when one is given and ignores ALLOWED_TAGS entirely, which silently reinstated `a`, `img`, and
    // `style` — the html profile permits all three. A spec asserting that no anchor survives caught
    // it. The explicit allow-list above is the only tag policy this renderer has.
  });
}

/**
 * The result of rendering one report body.
 *
 * A single HTML string rather than a list of segments, and that is the point. Markers are resolved
 * by walking the parsed DOM's text nodes and substituting placeholder elements, so the markup stays
 * balanced and the trusted string is the sanitized document itself. Splitting the serialized string
 * also broke ordinary markup whenever a marker sat inside a table cell or list item: each half was
 * reparsed alone, and `<td>before [^e1] after</td>` lost its table entirely.
 */
export interface RenderedReportBody {
  /**
   * Sanitized HTML with a placeholder element in place of each resolved citation marker.
   *
   * Safe to trust: it is the output of {@link sanitizeReportMarkdown} with elements this module
   * created itself spliced into text nodes. No part of it originated as model-authored markup.
   */
  html: string;
  /** IDs referenced by prose that no citation supplied, in first-appearance order. */
  unknownCitationIds: string[];
}

/**
 * Attribute naming the citation a placeholder stands for.
 *
 * A `data-` attribute is deliberately absent from {@link ALLOWED_ATTR}, so a placeholder cannot be
 * forged from model prose: any such attribute written in the statement is stripped during
 * sanitization, before this module inserts its own.
 */
export const CITATION_PLACEHOLDER_ATTRIBUTE = 'data-cce-citation';

/** Class on the inert element shown where a marker names evidence the report does not supply. */
export const UNRESOLVED_CITATION_CLASS = 'citation-unresolved';

/**
 * Attribute naming the code section an inline reference placeholder stands for (Issue #1773).
 *
 * Separate from {@link CITATION_PLACEHOLDER_ATTRIBUTE} because the two resolve against different
 * things: an evidence citation is looked up by ID in the report's own evidence chain, while a code
 * reference is validated against the report's catalog identity and carries no chain entry at all.
 * Keeping them distinct means neither resolver can be handed the other's placeholder.
 *
 * Like the citation attribute, this one is absent from {@link ALLOWED_ATTR}, so a placeholder cannot
 * be forged in model prose: any such attribute written in the statement is stripped during
 * sanitization, before this module inserts its own.
 */
export const SECTION_PLACEHOLDER_ATTRIBUTE = 'data-cce-section';

/**
 * Citation markers in generated Markdown.
 *
 * The footnote-style form is deliberate: `marked` has no footnote support configured here, so it
 * passes `[^e1]` through as literal text rather than restructuring the document, and the ID charset
 * matches the one the mapper enforces. Keeping the two in step means a marker can never carry a
 * character the resolver would have rejected.
 */
const CITATION_MARKER_PATTERN = /\[\^([A-Za-z0-9_-]{1,64})\]/g;

/**
 * Every marker form a statement may carry, as one pass over the text.
 *
 * Three alternatives, in this order: an evidence-chain marker (`[^e1]`), an inline code-section
 * marker (`[^sec:905.3.1]`), and an unresolved generation placeholder. Scanning once rather than
 * three times matters for correctness, not speed: three passes would each rewrite the text the next
 * one reads, so a marker could be found inside text a previous pass had already substituted.
 *
 * The evidence-chain ID charset excludes the colon, so the first two alternatives cannot match the
 * same text however they are ordered.
 */
const ALL_MARKERS_PATTERN = new RegExp(
  [
    CITATION_MARKER_PATTERN.source,
    INLINE_SECTION_MARKER_PATTERN.source,
    UNRESOLVED_SECTION_PLACEHOLDER_PATTERN.source,
  ].join('|'),
  'gi'
);

/**
 * Whether one text node contains anything this module should substitute.
 *
 * Built fresh on each call rather than reusing a `/g` pattern. A global regular expression carries
 * `lastIndex` between calls, and `test()` advances it, so a shared instance asked about a sequence of
 * text nodes starts each search part-way through the string and can report "no marker" for a node
 * whose marker sits before wherever the previous node happened to leave the cursor.
 */
function hasAnyMarker(text: string): boolean {
  // The PARSED document carries sentinels, not markers: the brackets were removed before parsing so
  // that Markdown could not read a marker as the first half of a link.
  return new RegExp(SENTINEL_PATTERN.source, 'i').test(text);
}

/** Elements inside which a citation marker is literal text rather than a citation. */
const LITERAL_TEXT_ELEMENTS = new Set(['PRE', 'CODE']);

/**
 * Renders a report body into sanitized HTML whose citation markers have become placeholders.
 *
 * Resolution happens on the parsed DOM, over text nodes only, which is what makes it safe. A marker
 * can therefore only ever be replaced where a reader would actually have seen it as text — never
 * inside a tag, an attribute value, or any other position where substituting it would change the
 * structure of the document.
 *
 * Markers are matched after sanitization rather than before, so a marker in a passage the sanitizer
 * removed disappears with it and no citation is rendered for text nobody can read.
 *
 * A marker inside a code block or code span stays literal. Compliance statements quote identifiers
 * and sometimes the citation syntax itself, and turning such a quotation into a live link would be
 * both wrong and confusing.
 */
export async function renderReportBody(markdown: string, citations: CitationSet): Promise<RenderedReportBody> {
  const clean = await sanitizeReportMarkdown(markdown);
  if (!clean) return { html: '', unknownCitationIds: [] };

  // Parsing into a detached element is safe because `clean` has already been sanitized, and it is
  // what lets every subsequent decision be made against structure instead of against text offsets.
  const host = document.createElement('div');
  host.innerHTML = clean;

  const unknownCitationIds: string[] = [];
  for (const textNode of collectCitationTextNodes(host)) {
    replaceMarkersInTextNode(textNode, citations, unknownCitationIds);
  }

  return { html: host.innerHTML, unknownCitationIds };
}

/**
 * Collects the text nodes a marker may legitimately appear in.
 *
 * Gathered into an array before any mutation, because replacing a text node while a `TreeWalker` is
 * positioned on it invalidates the walk.
 */
function collectCitationTextNodes(host: HTMLElement): Text[] {
  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, {
    acceptNode: (node: Node) =>
      hasLiteralTextAncestor(node, host) || !hasAnyMarker(node.nodeValue ?? '')
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });

  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    nodes.push(node as Text);
  }
  return nodes;
}

/** Whether a node sits inside an element whose contents are literal text. */
function hasLiteralTextAncestor(node: Node, host: HTMLElement): boolean {
  for (let parent = node.parentElement; parent && parent !== host; parent = parent.parentElement) {
    if (LITERAL_TEXT_ELEMENTS.has(parent.tagName)) return true;
  }
  return false;
}

/**
 * Substitutes one text node's markers with placeholder elements, leaving surrounding text intact.
 *
 * An unresolved marker becomes a visible inert element rather than disappearing. A reader who can
 * see the claim is entitled to know that the evidence behind it could not be resolved; silently
 * deleting the marker would make an unsupported claim look unremarkable.
 */
function replaceMarkersInTextNode(textNode: Text, citations: CitationSet, unknownCitationIds: string[]): void {
  const text = textNode.nodeValue ?? '';
  const replacement = document.createDocumentFragment();
  let cursor = 0;

  SENTINEL_PATTERN.lastIndex = 0;
  for (let match = SENTINEL_PATTERN.exec(text); match !== null; match = SENTINEL_PATTERN.exec(text)) {
    if (match.index > cursor) {
      replacement.appendChild(document.createTextNode(text.slice(cursor, match.index)));
    }
    cursor = match.index + match[0].length;

    // The payload is the marker without its brackets, so each form is recognised by its own shape.
    // These three patterns are anchored versions of the three the encoder matched; keeping them
    // beside each other is what makes it checkable that nothing can be encoded and then not decoded.
    const payload = match[1];
    const inlineSection = INLINE_SECTION_PAYLOAD.exec(payload);
    const unresolved = UNRESOLVED_PLACEHOLDER_PAYLOAD.exec(payload);
    const citationId = CITATION_PAYLOAD.exec(payload)?.[1];

    if (inlineSection) {
      replacement.appendChild(createSectionPlaceholder(inlineSection[1]));
      continue;
    }

    if (unresolved) {
      // A placeholder the backend did not resolve. The number is what the writer meant, so that is
      // what the reader sees; the scheme itself is never shown.
      replacement.appendChild(document.createTextNode(unresolved[1] ?? payload));
      continue;
    }

    if (citationId && citations.byId.has(citationId)) {
      replacement.appendChild(createPlaceholder(citationId));
      continue;
    }

    replacement.appendChild(createUnresolvedMarker());
    if (citationId && !unknownCitationIds.includes(citationId)) unknownCitationIds.push(citationId);
  }

  if (cursor < text.length) {
    replacement.appendChild(document.createTextNode(text.slice(cursor)));
  }
  textNode.replaceWith(replacement);
}

/**
 * An element marking where an inline code-section link belongs (Issue #1773).
 *
 * Carries the section number and the number as its text. The text matters: if nothing ever attaches
 * a link — a report whose catalog identity is incomplete, or a reference in another chapter — the
 * reader still reads the number in the sentence. The placeholder degrades to the words it replaced
 * rather than to a gap.
 */
function createSectionPlaceholder(sectionNumber: string): HTMLElement {
  const placeholder = document.createElement('span');
  placeholder.setAttribute(SECTION_PLACEHOLDER_ATTRIBUTE, sectionNumber);
  placeholder.textContent = sectionNumber;
  return placeholder;
}

/** An empty element marking where a citation component belongs. */
function createPlaceholder(citationId: string): HTMLElement {
  const placeholder = document.createElement('span');
  placeholder.setAttribute(CITATION_PLACEHOLDER_ATTRIBUTE, citationId);
  return placeholder;
}

/**
 * The inert element shown where prose references evidence this report does not supply.
 *
 * Built through DOM APIs rather than an HTML string, so nothing here can be influenced by the
 * marker's own text.
 */
function createUnresolvedMarker(): HTMLElement {
  const marker = document.createElement('span');
  marker.className = UNRESOLVED_CITATION_CLASS;
  marker.setAttribute(
    'title',
    'This report references a source it does not include, so the evidence behind this statement could not be opened.'
  );
  marker.setAttribute('aria-label', 'Source unavailable: this report references evidence it does not include.');
  return marker;
}
